import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { Search, ArrowRight, Eye, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

export default function ArticlesPage() {
  const { language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const loadArticles = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit: 9 });
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      const { data } = await api.get(`/articles?${params}`);
      setArticles(data.articles); setTotal(data.total); setPages(data.pages);
    } catch { setArticles([]); }
  }, [page, search, category]);

  useEffect(() => { loadArticles(); }, [loadArticles]);
  useEffect(() => { api.get('/articles/categories').then(r => setCategories(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="articles-page">
      <section className="py-24 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-medium">{language === 'tr' ? 'Hukuki Makaleler' : 'Legal Articles'}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F3F4F6] mt-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{language === 'tr' ? 'Makaleler' : 'Articles'}</h1>
          <p className="text-[#A1A1AA] mt-4 max-w-xl mx-auto">{language === 'tr' ? 'Hukuki gündem, bilgilendirme yazıları ve uzman görüşleri' : 'Legal agenda, informative articles and expert opinions'}</p>
        </div>
      </section>
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4 mb-12" data-testid="articles-filters">
            <form onSubmit={e => { e.preventDefault(); setSearch(searchInput); setPage(1); }} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#52525B]" />
                <Input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder={language === 'tr' ? 'Makale ara...' : 'Search articles...'} className="bg-[#121212] border-white/10 text-[#F3F4F6] pl-10 rounded-sm placeholder:text-[#52525B]" data-testid="articles-search" />
              </div>
              <Button type="submit" className="bg-[#C5A059] text-[#0A0A0A] hover:bg-[#D4AF6A] rounded-sm"><Search className="h-4 w-4" /></Button>
            </form>
            <Select value={category} onValueChange={v => { setCategory(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="bg-[#121212] border-white/10 text-[#F3F4F6] rounded-sm w-full sm:w-64" data-testid="articles-category-filter">
                <SelectValue placeholder={language === 'tr' ? 'Tüm Kategoriler' : 'All Categories'} />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                <SelectItem value="all" className="text-[#F3F4F6]">{language === 'tr' ? 'Tüm Kategoriler' : 'All Categories'}</SelectItem>
                {categories.map(c => <SelectItem key={c.slug} value={c.slug} className="text-[#F3F4F6]">{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <motion.div initial="hidden" animate="visible" variants={stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, i) => (
              <motion.div key={article.id || i} variants={fadeUp}>
                <Link to={`/articles/${article.slug}`} className="block bg-[#121212] border border-white/10 rounded-sm overflow-hidden card-hover group" data-testid={`article-card-${i}`}>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Badge className="bg-[#C5A059]/10 text-[#C5A059] border-[#C5A059]/20 text-xs">{article.category}</Badge>
                      {article.is_featured && <Badge className="bg-[#C5A059] text-[#0A0A0A] text-xs">{language === 'tr' ? 'Öne Çıkan' : 'Featured'}</Badge>}
                    </div>
                    <h3 className="text-xl font-semibold text-[#F3F4F6] mb-3 group-hover:text-[#C5A059] transition-colors line-clamp-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{article.title}</h3>
                    <p className="text-[#A1A1AA] text-sm line-clamp-3 mb-4">{article.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-[#52525B] text-xs">
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(article.created_at).toLocaleDateString('tr-TR')}</span>
                        <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {article.view_count || 0}</span>
                      </div>
                      <span className="text-[#C5A059] text-sm flex items-center gap-1 group-hover:gap-2 transition-all">{language === 'tr' ? 'Devamını Oku' : 'Read More'} <ArrowRight className="h-4 w-4" /></span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
          {articles.length === 0 && <div className="text-center py-16 text-[#A1A1AA]">{language === 'tr' ? 'Makale bulunamadı.' : 'No articles found.'}</div>}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-white/10 text-[#A1A1AA] hover:text-[#C5A059] rounded-sm"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-[#A1A1AA] text-sm px-4">{page} / {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="border-white/10 text-[#A1A1AA] hover:text-[#C5A059] rounded-sm"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
          <div className="text-center mt-6 text-[#52525B] text-xs">{language === 'tr' ? `Toplam ${total} makale` : `Total ${total} articles`}</div>
        </div>
      </section>
    </div>
  );
}
